'use client';

import { useEffect } from 'react';
import { DataLayerConfig, Locale } from '@headless-workspace/config';
import { CookieHelpers, LanguageHelpers } from '@headless-workspace/utils';
import { DataLayerYesNo } from '../../../../client';
import { DataLayerIsConnected, DataLayerKey, useDataLayer } from '../../../../client';
import { ConnectedUserInfo, hasFidelityProgram, isConnectedUserInfo } from '../../../../server';
import { getCustomerCategory, getUserLoyaltyCategory } from '../DataLayerEventParamsFactory';
import { DataLayerUtils } from '../DataLayerUtils';

const EMPTY_VALUE = '';
const VALUE_FOR_UNLOGGED_USER = '-1';
const USER_ID_KEY = 'encrypteduserid';

interface UseHeaderDataLayerVariablesProps {
    locale: Locale;
    device: string;
    buildEnv: string;
    addVariables: ReturnType<typeof useDataLayer>['addVariables'];
    userInfo?: ConnectedUserInfo;
}

export const useHeaderDataLayerVariables = ({
    locale,
    device,
    addVariables,
    buildEnv,
    userInfo,
}: UseHeaderDataLayerVariablesProps) => {
    const language = LanguageHelpers.parseLanguage(locale);
    const countryCode = LanguageHelpers.parseCountry(locale).toLowerCase();

    // Set basic environment variables once
    useEffect(() => {
        addVariables({
            [DataLayerKey.Country]: countryCode,
            [DataLayerKey.EnvCountry]: countryCode,
            [DataLayerKey.EnvLanguage]: language,
            [DataLayerKey.EnvWork]: DataLayerConfig.envWork[buildEnv],
        });
    }, [addVariables, countryCode, language, buildEnv]);

    // Update device/platform variables when device changes
    useEffect(() => {
        addVariables({
            // No distinction between desktopS and desktop in this datalayer
            [DataLayerKey.EnvPlatform]: device === 'desktopS' ? 'desktop' : device,
        });
    }, [addVariables, device]);

    // Update user-related variables when userInfo changes
    useEffect(() => {
        const isUserConnected = isConnectedUserInfo(userInfo);
        const loyaltyCategory = getUserLoyaltyCategory(userInfo).toLowerCase();

        // Base user variables
        const baseUserVariables = {
            [DataLayerKey.UserLogged]: DataLayerUtils.mapBooleanToYesNo(isUserConnected),
            [DataLayerKey.UserIdentified]: isUserConnected
                ? DataLayerIsConnected.Authenticated
                : DataLayerIsConnected.Anonymous,
            [DataLayerKey.UserLoyaltyCategory]: loyaltyCategory,
        };

        if (userInfo) {
            // Connected user variables
            const hasFidelity = hasFidelityProgram(userInfo);
            const isSubscribed = userInfo.isSubscribedByEmail;
            const customerCategory = getCustomerCategory(userInfo);
            const customerEncryptedEmail = userInfo.customerEncryptedEmail;
            const userPurchaseFrequency = userInfo.ordersCount ? String(userInfo.ordersCount) : EMPTY_VALUE;
            const userStore = DataLayerUtils.mapStringToYesNo(userInfo.favoriteStoreId);

            const connectedUserVariables = {
                [DataLayerKey.UserCardOwner]: DataLayerUtils.mapBooleanToYesNo(hasFidelity),
                [DataLayerKey.UserEmailOptin]: DataLayerUtils.mapBooleanToYesNo(isSubscribed),
                [DataLayerKey.UserEmail]: customerEncryptedEmail,
                [DataLayerKey.UserNewCustomer]: customerCategory,
                [DataLayerKey.UserPurchaseFrequency]: userPurchaseFrequency,
                [DataLayerKey.UserStore]: userStore,
                [DataLayerKey.UserGender]: userInfo.gender ?? EMPTY_VALUE,
                [DataLayerKey.UserType]: userInfo.type ?? EMPTY_VALUE,
                [DataLayerKey.FavoriteOrderAddressDeclared]: DataLayerUtils.mapBooleanToYesNo(
                    userInfo.hasFavoriteAddress,
                ),
                [DataLayerKey.FavoriteCreditCardDeclared]: DataLayerUtils.mapBooleanToYesNo(
                    userInfo.hasFavoriteCreditCard,
                ),
                [DataLayerKey.UserWithOffer]: DataLayerUtils.mapBooleanToYesNo(userInfo.hasOffers),
                [DataLayerKey.UserInscriptionDate]: userInfo.inscriptionDate ?? EMPTY_VALUE,
                [DataLayerKey.UserId]: CookieHelpers.getClientSideCookie(USER_ID_KEY),
            };

            addVariables({
                ...baseUserVariables,
                ...connectedUserVariables,
            });
        } else {
            const anonymousUserVariables = {
                [DataLayerKey.UserStore]: VALUE_FOR_UNLOGGED_USER,
                [DataLayerKey.FavoriteOrderAddressDeclared]: VALUE_FOR_UNLOGGED_USER,
                [DataLayerKey.FavoriteCreditCardDeclared]: VALUE_FOR_UNLOGGED_USER,
                [DataLayerKey.UserCardOwner]: DataLayerYesNo.No,
            };

            addVariables({
                ...baseUserVariables,
                ...anonymousUserVariables,
            });
        }
    }, [addVariables, userInfo]);
};
